import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Button, Typography, Popconfirm, Spin } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import FourColumnRowSection from "components/common/FourColumnRowSection/FourColumnRowSection";
import { DependentEditSection } from "components/staff/DependentEditSection/DependentEditSection";
import showAlertNotification from "components/common/Modal/AlertNotification";
import { TrashIcon } from "@phosphor-icons/react";
import { ImagePayload } from "types/image.type";
import { useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  useGetAllDependentsQuery,
  useCreateDependentMutation,
  useUpdateDependentMutation,
  useDeleteDependentMutation,
} from "services/dependent.service";
import { DependentModel } from "types/dependent.types";
import UploadImageButton from "components/common/UploadImageButton/UploadButton";

const { Text } = Typography;

interface DependentInfoTabProps {
  isEditing?: boolean;
}

interface NewDependent extends Partial<DependentModel> {
  frontImageData: ImagePayload[];
  backImageData: ImagePayload[];
}

type ExistingImageState = Record<
  string,
  { front: ImagePayload[]; back: ImagePayload[] }
>;

const DependentInfoTab = forwardRef<
  { submitAll: () => Promise<void> },
  DependentInfoTabProps
>(({ isEditing = false }, ref) => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const employeeType = searchParams.get("type") || "staff";

  const [newDependents, setNewDependents] = useState<NewDependent[]>([]);
  const [existingImages, setExistingImages] = useState<ExistingImageState>({});

  const queryFilter =
    employeeType === "staff" ? { staffId: id } : { driverId: id };

  const {
    data: dependentsRes,
    isLoading,
    refetch,
  } = useGetAllDependentsQuery({ query: queryFilter }, { skip: !id });

  const [createDependent] = useCreateDependentMutation();
  const [updateDependent] = useUpdateDependentMutation();
  const [deleteDependent] = useDeleteDependentMutation();

  const existingDependents = dependentsRes?.data?.rows || [];

  const genderMap: Record<string, string> = {
    Nam: "male",
    Nữ: "female",
    Khác: "other",
  };

  const genderDisplayMap: Record<string, string> = {
    male: "Nam",
    female: "Nữ",
    other: "Khác",
  };

  const getExistingImageState = (item: DependentModel) => {
    if (!existingImages[item.id]) {
      return {
        front: item.frontImage
          ? [{ filePath: item.frontImage, name: "front", order: 1 }]
          : [],
        back: item.backImage
          ? [{ filePath: item.backImage, name: "back", order: 1 }]
          : [],
      };
    }
    return existingImages[item.id];
  };

  const handleExistingImageChange = async (
    depId: string,
    field: "frontImage" | "backImage",
    images: ImagePayload[],
    side: "front" | "back",
  ) => {
    setExistingImages((prev: ExistingImageState) => ({
      ...prev,
      [depId]: {
        ...prev[depId],
        [side]: images,
      },
    }));

    const imageUrl = images[0]?.filePath || "";
    try {
      await updateDependent({ id: depId, [field]: imageUrl }).unwrap();
    } catch {
      showAlertNotification({
        type: "error",
        title: "Lỗi",
        message: "Không thể cập nhật ảnh",
      });
    }
  };

  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {},
  );

  const handleUpdateExisting = async (
    dependent: DependentModel,
    path: string,
    value: string | ImagePayload[],
  ) => {
    const key = path.split(".").pop()!;
    const fieldMap: Record<string, string> = {
      fullName: "name",
      birthDate: "birthday",
      idCard: "citizenId",
      idIssueDate: "citizenIdDate",
      idIssuePlace: "citizenIdPlace",
      taxCode: "taxNumber",
      gender: "gender",
      relationship: "relationship",
    };
    const apiKey = fieldMap[key] ?? key;

    const finalValue =
      apiKey === "gender" && typeof value === "string"
        ? (genderMap[value] ?? value)
        : value;

    // ✅ Debounce 600ms, cancel call cũ nếu có
    const timerKey = `${dependent.id}-${apiKey}`;
    if (debounceTimers.current[timerKey]) {
      clearTimeout(debounceTimers.current[timerKey]);
    }

    debounceTimers.current[timerKey] = setTimeout(async () => {
      try {
        await updateDependent({
          id: dependent.id,
          [apiKey]: finalValue ?? "",
        }).unwrap();
      } catch {
        showAlertNotification({
          type: "error",
          title: "Lỗi",
          message: "Không thể cập nhật thông tin",
        });
      }
    }, 600);
  };

  const handleAddDependent = () => {
    setNewDependents((prev) => [
      ...prev,
      {
        name: "",
        relationship: "",
        birthday: null,
        gender: undefined,
        citizenId: "",
        citizenIdDate: null,
        citizenIdPlace: "",
        taxNumber: "",
        frontImage: null,
        backImage: null,
        frontImageData: [],
        backImageData: [],
      },
    ]);
  };

  const handleUpdateNew = (
    index: number,
    path: string,
    value: string | ImagePayload[],
  ) => {
    const key = path.split(".").pop()!;

    const fieldMap: Record<string, string> = {
      fullName: "name",
      birthDate: "birthday",
      idCard: "citizenId",
      idIssueDate: "citizenIdDate",
      idIssuePlace: "citizenIdPlace",
      taxCode: "taxNumber",
      gender: "gender",
      relationship: "relationship",
    };
    const stateKey = fieldMap[key] ?? key;

    setNewDependents((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [stateKey]: value ?? "" };
      return updated;
    });
  };

  const handleNewImageChange = (
    index: number,
    side: "frontImageData" | "backImageData",
    images: ImagePayload[],
  ) => {
    setNewDependents((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [side]: images };
      return updated;
    });
  };

  const handleSaveNew = async (index: number) => {
    const dep = newDependents[index];
    const genderMap: Record<string, string> = {
      Nam: "male",
      Nữ: "female",
      Khác: "other",
    };

    if (!dep.name) {
      showAlertNotification({
        type: "error",
        title: "Thiếu thông tin",
        message: "Vui lòng nhập tên người phụ thuộc",
      });
      return;
    }
    try {
      await createDependent({
        ...(employeeType === "staff" ? { staffId: id } : { driverId: id }),
        name: dep.name || "",
        relationship: dep.relationship || undefined,
        birthday: dep.birthday || undefined,
        gender: dep.gender
          ? (genderMap[dep.gender] as "male" | "female" | "other")
          : undefined,
        citizenId: dep.citizenId || undefined,
        citizenIdDate: dep.citizenIdDate || undefined,
        citizenIdPlace: dep.citizenIdPlace || undefined,
        taxNumber: dep.taxNumber || undefined,
        frontImage: dep.frontImageData?.[0]?.filePath || undefined,
        backImage: dep.backImageData?.[0]?.filePath || undefined,
      }).unwrap();

      setNewDependents((prev) => prev.filter((_, i) => i !== index));
      refetch();
      showAlertNotification({
        type: "success",
        title: "Thành công",
        message: "Đã thêm người phụ thuộc",
      });
    } catch {
      showAlertNotification({
        type: "error",
        title: "Lỗi",
        message: "Không thể thêm người phụ thuộc",
      });
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      submitAll: async () => {
        for (let i = 0; i < newDependents.length; i++) {
          await handleSaveNew(i);
        }
      },
    }),
    [newDependents],
  );

  const handleDeleteExisting = async (depId: string) => {
    try {
      await deleteDependent({ id: depId }).unwrap();
      refetch();
      showAlertNotification({
        type: "success",
        title: "Đã xoá",
        message: "Người phụ thuộc đã được xoá",
      });
    } catch {
      showAlertNotification({
        type: "error",
        title: "Lỗi",
        message: "Không thể xóa người phụ thuộc",
      });
    }
  };

  const handleRemoveNew = (index: number) => {
    setNewDependents((prev) => prev.filter((_, i) => i !== index));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spin tip="Đang tải dữ liệu người phụ thuộc..." />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 px-0">
      {/* ── DANH SÁCH EXISTING ── */}
      {existingDependents.map((item) => {
        const imgState = getExistingImageState(item);
        return isEditing ? (
          <div key={item.id} className="bg-white pt-3 pb-4 rounded-2xl">
            <DependentEditSection
              index={0}
              data={{
                id: item.id,
                fullName: item.name,
                relationship: item.relationship || "",
                birthDate: item.birthday || "",
                gender:
                  genderDisplayMap[item.gender || ""] || item.gender || "",
                idCard: item.citizenId || "",
                idIssueDate: item.citizenIdDate || "",
                idIssuePlace: item.citizenIdPlace || "",
                taxCode: item.taxNumber || "",
              }}
              onUpdate={(path, value) =>
                handleUpdateExisting(item, path, value as string)
              }
              onRemove={() => handleDeleteExisting(item.id)}
              isUploading={false}
            />

            <div className="px-6 pt-3 pb-2">
              <p className="text-sm text-gray-500 mb-2">Ảnh chứng từ</p>
              <div className="flex gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Mặt trước</p>
                  <UploadImageButton
                    imagesData={imgState.front}
                    setImagesData={(
                      updater: React.SetStateAction<ImagePayload[]>,
                    ) => {
                      const newImages =
                        typeof updater === "function"
                          ? updater(imgState.front)
                          : updater;
                      handleExistingImageChange(
                        item.id,
                        "frontImage",
                        newImages,
                        "front",
                      );
                    }}
                    maxCount={1}
                    label="Tải ảnh mặt trước"
                    isDisabled={false}
                  />
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Mặt sau</p>
                  <UploadImageButton
                    imagesData={imgState.back}
                    setImagesData={(
                      updater: React.SetStateAction<ImagePayload[]>,
                    ) => {
                      const newImages =
                        typeof updater === "function"
                          ? updater(imgState.back)
                          : updater;
                      handleExistingImageChange(
                        item.id,
                        "backImage",
                        newImages,
                        "back",
                      );
                    }}
                    maxCount={1}
                    label="Tải ảnh mặt sau"
                    isDisabled={false}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-4 px-6 border-t border-gray-100 pt-4">
              <Popconfirm
                title="Bạn có chắc chắn muốn xóa người phụ thuộc này?"
                onConfirm={() => handleDeleteExisting(item.id)}
                okText="Xóa"
                cancelText="Hủy"
              >
                <Button danger>
                  <TrashIcon /> Xóa người phụ thuộc
                </Button>
              </Popconfirm>
            </div>
          </div>
        ) : (
          <div
            key={item.id}
            className="relative bg-white py-3 pb-6 rounded-2xl"
          >
            <FourColumnRowSection
              leftTitle={
                <div className="flex items-center gap-4 min-w-[400px]">
                  <Text strong className="uppercase w-[228px]">
                    {item.name}
                  </Text>
                  <Text className="normal-case font-normal">
                    Quan hệ: {item.relationship}
                  </Text>
                </div>
              }
              rightTitle=""
              rows={[
                {
                  leftLabel: "Ngày sinh",
                  leftValue: item.birthday
                    ? dayjs(item.birthday).format("DD/MM/YYYY")
                    : "---",
                  rightLabel: "Giới tính",
                  rightValue:
                    genderDisplayMap[item.gender || ""] || item.gender || "",
                },
                {
                  leftLabel: "CCCD/CMND",
                  leftValue: item.citizenId || "---",
                  rightLabel: "Ngày cấp",
                  rightValue: item.citizenIdDate
                    ? dayjs(item.citizenIdDate).format("DD/MM/YYYY")
                    : "---",
                },
                {
                  leftLabel: "Nơi cấp",
                  leftValue: item.citizenIdPlace || "---",
                  rightLabel: "MST NPT",
                  rightValue: item.taxNumber || "---",
                },
              ]}
            />
            {(item.frontImage || item.backImage) && (
              <div className="px-6 pt-3 flex gap-4">
                {item.frontImage && (
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Mặt trước</p>
                    <img
                      src={item.frontImage}
                      alt="Mặt trước"
                      className="w-24 h-16 object-cover rounded border"
                    />
                  </div>
                )}
                {item.backImage && (
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Mặt sau</p>
                    <img
                      src={item.backImage}
                      alt="Mặt sau"
                      className="w-24 h-16 object-cover rounded border"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* ── DANH SÁCH NEW (chưa lưu) ── */}
      {isEditing &&
        newDependents.map((dep, index) => (
          <div
            key={index}
            className="bg-white pt-3 pb-4 rounded-2xl border-2 border-blue-200"
          >
            <DependentEditSection
              index={index}
              data={{
                id: "",
                fullName: dep.name || "",
                relationship: dep.relationship || "",
                birthDate: dep.birthday || "",
                gender: dep.gender || "",
                idCard: dep.citizenId || "",
                idIssueDate: dep.citizenIdDate || "",
                idIssuePlace: dep.citizenIdPlace || "",
                taxCode: dep.taxNumber || "",
              }}
              onUpdate={(path, value) =>
                handleUpdateNew(index, path, value as string)
              }
              onRemove={() => handleRemoveNew(index)}
              isUploading={false}
            />

            <div className="px-6 pt-3 pb-2">
              <p className="text-sm text-gray-500 mb-2">Ảnh chứng từ</p>
              <div className="flex gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Mặt trước</p>
                  <UploadImageButton
                    imagesData={dep.frontImageData}
                    setImagesData={(
                      updater: React.SetStateAction<ImagePayload[]>,
                    ) => {
                      const newImages =
                        typeof updater === "function"
                          ? updater(dep.frontImageData)
                          : updater;
                      handleNewImageChange(index, "frontImageData", newImages);
                    }}
                    maxCount={1}
                    label="Tải ảnh mặt trước"
                    isDisabled={false}
                  />
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">Mặt sau</p>
                  <UploadImageButton
                    imagesData={dep.backImageData}
                    setImagesData={(
                      updater: React.SetStateAction<ImagePayload[]>,
                    ) => {
                      const newImages =
                        typeof updater === "function"
                          ? updater(dep.backImageData)
                          : updater;
                      handleNewImageChange(index, "backImageData", newImages);
                    }}
                    maxCount={1}
                    label="Tải ảnh mặt sau"
                    isDisabled={false}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-4 px-6 border-t border-gray-100 pt-4">
              <Button onClick={() => handleRemoveNew(index)}>Hủy</Button>
              <Button type="primary" onClick={() => handleSaveNew(index)}>
                Lưu người phụ thuộc
              </Button>
            </div>
          </div>
        ))}

      {/* ── NÚT THÊM NPT ── */}
      {isEditing && (
        <div className="pt-2">
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            className="text-blue-500 border-blue-300 bg-blue-50 w-full rounded-xl py-5"
            onClick={handleAddDependent}
          >
            Thêm người phụ thuộc mới
          </Button>
        </div>
      )}
    </div>
  );
});
DependentInfoTab.displayName = "DependentInfoTab";
export default DependentInfoTab;
